/**
 * UpgradeConfirmationModal Component
 *
 * Shows prorated upgrade cost and confirmation before charging user
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface UpgradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  currentPlan: string;
  newPlan: string;
  proratedAmount: number | null;
  fullAmount: number;
}

export function UpgradeConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  currentPlan,
  newPlan,
  proratedAmount,
  fullAmount
}: UpgradeConfirmationModalProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const displayAmount = proratedAmount !== null ? proratedAmount : fullAmount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirm Plan Upgrade</DialogTitle>
          <DialogDescription>
            You're about to upgrade from {currentPlan} to {newPlan}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pricing Breakdown */}
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {newPlan} plan (monthly)
              </span>
              <span className="font-medium">{formatCurrency(fullAmount)}</span>
            </div>

            {proratedAmount !== null && proratedAmount < fullAmount && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Credit for unused {currentPlan} time
                  </span>
                  <span className="font-medium text-green-600">
                    -{formatCurrency(fullAmount - proratedAmount)}
                  </span>
                </div>
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="font-semibold">Amount due today</span>
                  <span className="text-xl font-bold">{formatCurrency(proratedAmount)}</span>
                </div>
              </>
            )}

            {(proratedAmount === null || proratedAmount === fullAmount) && (
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="font-semibold">Amount due today</span>
                <span className="text-xl font-bold">{formatCurrency(fullAmount)}</span>
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="space-y-2">
            <p className="text-sm font-medium">What happens next:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Your card will be charged {formatCurrency(displayAmount)} immediately</li>
              <li>You'll get instant access to all {newPlan} features</li>
              <li>Your billing cycle will reset to today</li>
              <li>Next billing date: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              `Confirm & Pay ${formatCurrency(displayAmount)}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
